import { useState } from 'react';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Drawer, Tabs, TabsList, TabsTrigger } from '../../ui/index.ts';
import { RoutingLoadScopeView } from './RoutingLoadScopeView.tsx';
import { RoutingLoadSettingsPanel } from './RoutingLoadSettingsPanel.tsx';
import { RoutingLoadTargetsView } from './RoutingLoadTargetsView.tsx';

type LoadDrawerView = 'settings' | 'scope' | 'runtime';

const VIEW_TITLE: Record<LoadDrawerView, I18nKey> = {
  settings: 'tenant_routing.load.settings.title',
  scope: 'tenant_routing.load.scope.title',
  runtime: 'tenant_routing.load.runtime.title',
};

export function RoutingLoadDrawer({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): JSX.Element {
  const t = useT();
  const [view, setView] = useState<LoadDrawerView>('settings');

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setView('settings');
          onClose();
        }
      }}
      width="xl"
      title={t(VIEW_TITLE[view])}
      description={t('tenant_routing.load.drawer_hint')}
      closeLabel={t('common.close')}
      testId="routing-load-drawer"
    >
      <div className="mb-3">
        <Tabs
          value={view}
          onValueChange={(next) => {
            if (next === 'settings' || next === 'scope' || next === 'runtime') {
              setView(next);
            }
          }}
        >
          <TabsList ariaLabel={t('tenant_routing.load.drawer_hint')}>
            <TabsTrigger value="settings" testId="routing-load-view-settings">
              {t('tenant_routing.load.view.settings')}
            </TabsTrigger>
            <TabsTrigger value="scope" testId="routing-load-view-scope">
              {t('tenant_routing.load.view.scope')}
            </TabsTrigger>
            <TabsTrigger value="runtime" testId="routing-load-view-runtime">
              {t('tenant_routing.load.view.runtime')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {view === 'settings' ? <RoutingLoadSettingsPanel /> : null}
      {view === 'scope' ? <RoutingLoadScopeView /> : null}
      {view === 'runtime' ? <RoutingLoadTargetsView /> : null}
    </Drawer>
  );
}
